import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EnrollStudentModal } from '@/components/schedule/EnrollStudentModal';
import { LessonShareModal } from '@/components/lessons/LessonShareModal';
import { resolvePublicLessonShareUrl } from '@court-sync/shared';
import { useAcademy } from '@/queries/academy';
import { lessonService, type LessonEnrollmentRow } from '@/services/lesson';

export interface PostCreateLessonStepProps {
  academyId: string;
  academySlug: string;
  lessonId: string;
  lessonName: string;
  maxCapacity?: number;
  onInlineFooterChange?: (footer: React.ReactNode | null) => void;
  onEnrollSuccess?: () => void | Promise<void>;
  onDone: () => void;
}

export function PostCreateLessonStep({
  academyId,
  academySlug: _academySlug,
  lessonId,
  lessonName,
  maxCapacity,
  onInlineFooterChange,
  onEnrollSuccess,
  onDone,
}: PostCreateLessonStepProps) {
  const { academy } = useAcademy(academyId);
  const [enrollments, setEnrollments] = useState<LessonEnrollmentRow[]>([]);
  const [shareOpen, setShareOpen] = useState(false);

  const shareBookingLink = useMemo(() => {
    if (!academy?.slug) return '';
    return resolvePublicLessonShareUrl(
      academy,
      { id: lessonId, lessonType: 'private', isActive: true },
      null
    );
  }, [academy, lessonId]);

  const refreshEnrollments = useCallback(async () => {
    try {
      const res = await lessonService.getLessonEnrollments(lessonId, { academyId });
      if (res.success && res.data) {
        setEnrollments(res.data);
      }
    } catch {
      // Enrollment count refresh is best-effort for the post-create step.
    }
  }, [academyId, lessonId]);

  const enrolledCount = enrollments.filter(
    (r) => r.bookingId && r.enrollmentSource !== 'makeup_session'
  ).length;

  useEffect(() => {
    if (!onInlineFooterChange) return undefined;
    onInlineFooterChange(
      <div className="flex w-full flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setShareOpen(true)}>
          <Share2 className="mr-1.5 h-4 w-4" aria-hidden />
          Share & invite
        </Button>
        <Button type="button" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    );
    return () => onInlineFooterChange(null);
  }, [onInlineFooterChange, onDone]);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50/80 p-4 dark:border-green-900/50 dark:bg-green-950/20">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" aria-hidden />
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{lessonName} created</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add students now, or share the booking link so guardians can book and pay online.
          </p>
        </div>
      </div>

      <EnrollStudentModal
        lessonId={lessonId}
        lessonName={lessonName}
        academyId={academyId}
        isOpen
        presentation="inline"
        stayOpenAfterEnroll
        hideInlineCancel
        shareBookingLink={shareBookingLink}
        maxCapacity={maxCapacity}
        enrolledCount={enrolledCount}
        onClose={onDone}
        onEnrollSuccess={async () => {
          await refreshEnrollments();
          await onEnrollSuccess?.();
        }}
        onInlineFooterChange={undefined}
      />

      <LessonShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        academyId={academyId}
        academy={academy ?? undefined}
        lessonId={lessonId}
        lessonName={lessonName}
        lessonType="private"
        enrollments={enrollments}
        onAfterNotify={refreshEnrollments}
      />
    </div>
  );
}
